import { resolveAvatarUrl } from '../utility/azure-blob-fetch';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import './sse-client-activity-monitor/sse-client-activity-monitor.js';

import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';
import '@shoelace-style/shoelace/dist/components/avatar/avatar.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import { write } from '../language/lang';
import { styles as sharedStyles } from '../styles/shared-styles';
// Styles live in a real .css file - see data-table.ts for the rationale.
import styles from './header-styles.css?inline';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { router, resolveRouterPath } from '../router';
import { resolveVisibleModuleIds } from '../modules/registry/module-registry';
import { sessionManager } from '../services/session-manager';
import type { RememberedUser } from '../datatypes/session-types';
import { userDirectoryInstance } from '../global-data/user-directory-instance';
import { awaitableTemplate } from '@/utility/awaitable-data.js';
import { terms } from '../language/terms';

@customElement('app-header')
export class AppHeader extends WithUserProfileConsumer(LitElement) {
  @property({ type: String }) pageTitle = write`Home`;

  @property({ type: Boolean }) enableBack: boolean = false;

  /** Unsubscribe function for session manager events */
  private _sessionUnsubscribe?: () => void;

  /** Unsubscribe function for user directory updates */
  private _userDirectoryUnsubscribe?: () => void;

  /** Currently subscribed user ID for cache updates */
  private _subscribedUserId?: string;

  /** Remembered users for switch account (from IndexedDB) */
  @state() private _rememberedUsers: RememberedUser[] = [];

  // Drawer removed; using inline dropdown instead.

  static styles = [sharedStyles, unsafeCSS(styles)];

  connectedCallback() {
    super.connectedCallback();
    // Subscribe to session events to update menu when sessions change
    this._sessionUnsubscribe = sessionManager.subscribe((event) => {
      this.requestUpdate();
      // Reload remembered users when sessions or remembered users change
      if (
        event.type === 'session-switched' ||
        event.type === 'session-added' ||
        event.type === 'session-removed' ||
        event.type === 'remembered-users-changed'
      ) {
        void this._loadRememberedUsers();
      }
    });
    // Subscribe to current user's directory updates
    this._subscribeToUserUpdates();
    // Load remembered users for switch account menu
    void this._loadRememberedUsers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._sessionUnsubscribe?.();
    this._userDirectoryUnsubscribe?.();
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    // Re-subscribe if user changes
    this._subscribeToUserUpdates();
  }

  /** Subscribe to user directory updates for the current user */
  private _subscribeToUserUpdates() {
    const userId = this.userProfile?.data?.userId;
    if (userId === this._subscribedUserId) return; // already subscribed to this user

    // Unsubscribe from previous user
    this._userDirectoryUnsubscribe?.();
    this._userDirectoryUnsubscribe = undefined;
    this._subscribedUserId = undefined;

    if (!userId) return;

    // Subscribe to updates for the current user
    const cache = userDirectoryInstance();
    this._userDirectoryUnsubscribe = cache.subscribe(userId, () => {
      this.requestUpdate();
    });
    this._subscribedUserId = userId;
  }

  /**
   * Load remembered users from IndexedDB
   */
  private async _loadRememberedUsers(): Promise<void> {
    try {
      this._rememberedUsers = await sessionManager.getRememberedUsers();
    } catch (err) {
      console.error('Failed to load remembered users:', err);
      this._rememberedUsers = [];
    }
  }

  /**
   * Get remembered users that are not the current user, with session status
   */
  private get switchableUsers(): Array<
    RememberedUser & { hasActiveSession: boolean; sessionId?: string }
  > {
    const currentUserId = this.userProfile?.data?.userId;
    const activeSessions = new Map(
      sessionManager.sessions.map((s) => [s.loginUserId, s])
    );

    return this._rememberedUsers
      .filter((user) => user.userId !== currentUserId)
      .map((user) => {
        const session = activeSessions.get(user.userId);
        return {
          ...user,
          hasActiveSession: !!session,
          sessionId: session?.sessionId ?? user.sessionId,
          // Use session's profile image if available (more up-to-date)
          profileImageUrl: session?.profileImageUrl ?? user.profileImageUrl,
        };
      });
  }

  private navigate(route: string) {
    void router!.navigate(resolveRouterPath(route));
  }

  // Submenu navigation removed; retained method name only if future reinstatement needed.
  // Drawer handler removed.

  private handleLogout() {
    this.dispatchEvent(
      new CustomEvent('logout', { bubbles: true, composed: true })
    );
  }

  private async handleSwitchAccount(
    user: RememberedUser & { hasActiveSession: boolean; sessionId?: string }
  ): Promise<void> {
    console.log('[header] handleSwitchAccount called', {
      user,
      hasActiveSession: user.hasActiveSession,
      sessionId: user.sessionId,
    });
    if (user.hasActiveSession && user.sessionId) {
      // Has active session - switch to it
      console.log('[header] Attempting session switch to', user.sessionId);
      const result = await sessionManager.switchSession(user.sessionId);
      if (result.success) {
        // Full page reload to home to clear all in-memory state from previous user
        window.location.href = '/';
      } else {
        console.error('Failed to switch session:', result.error);
        // Session may have expired - redirect to login with username pre-filled
        this.navigate(
          `login?addAccount=true&username=${encodeURIComponent(user.username)}`
        );
      }
    } else {
      // No active session - redirect to login with username pre-filled
      // Include addAccount=true to bypass redirect-if-logged-in plugin
      console.log(
        '[header] No active session, navigating to login for',
        user.username
      );
      this.navigate(
        `login?addAccount=true&username=${encodeURIComponent(user.username)}`
      );
    }
  }

  private handleAddAccount(): void {
    // Navigate to login page with addAccount flag to prevent redirect
    this.navigate('login?addAccount=true');
  }

  /**
   * Get profile image URL for a user ID from UserDirectory cache
   */
  private async getProfileImageUrl(userId: string | undefined) {
    if (!userId) return '/assets/images/profile-default.png';
    const cache = userDirectoryInstance();
    const user = await cache.getUserDto(userId);
    if (user?.profileImageUrl) {
      // SAS URLs from blob storage are absolute, relative paths need domain prefix
      // Shared resolver - see resolveAvatarUrl for why this is not a startsWith('http') test.
      return resolveAvatarUrl(user.profileImageUrl)!;
    }
    return '/assets/images/profile-default.png';
  }

  private async profileImageUrl() {
    const userId = this.userProfile?.data?.userId;
    return await this.getProfileImageUrl(userId);
  }

  private renderSwitchAccountSection() {
    // Show remembered users for quick switching
    // Users with active sessions switch instantly, others go to login with pre-filled username
    const users = this.switchableUsers;
    return html`
      ${
        users.length > 0
          ? html`
              <div class="menu-section-label">Switch Account</div>
              ${users.map((user) => {
                const avatar = awaitableTemplate(async () => {
                  const url = await this.getProfileImageUrl(user.userId);
                  return html`<sl-avatar
                    slot="prefix"
                    .image=${url}
                    label=${user.displayName}
                    style="--size: 24px;"
                  ></sl-avatar>`;
                });
                return html`
                  <sl-menu-item
                    class="switch-account-item"
                    @click=${() => this.handleSwitchAccount(user)}
                  >
                    ${avatar}
                    <span class="switch-account-name">
                      ${user.displayName}
                      ${
                        !user.hasActiveSession
                          ? html`<sl-icon
                              name="box-arrow-in-right"
                              style="margin-left: 0.5rem; opacity: 0.5;"
                            ></sl-icon>`
                          : nothing
                      }
                    </span>
                  </sl-menu-item>
                `;
              })}
            `
          : nothing
      }
      <sl-menu-item @click=${() => this.handleAddAccount()}>
        <sl-icon slot="prefix" name="plus-circle"></sl-icon>
        Add account
      </sl-menu-item>
      <sl-divider></sl-divider>
    `;
  }

  // Drawer content removed.

  // Legacy menu item renderer removed with drawer.

  private renderBrandBlock() {
    return html`
      <div class="brand-slot">
        <a class="brand" href="/" aria-label="Home">
          <img
            class="brand-logo"
            src="/assets/images/banner.svg"
            alt="${terms.agwa.value}"
          />
        </a>
        <!-- Inline title (md+). Duplicate of responsive title below; aria-hidden to avoid double announce -->
        ${
          this.pageTitle
            ? html`<h1 class="page-title-inline" aria-hidden="true">
                ${this.pageTitle}
              </h1>`
            : null
        }
      </div>
    `;
  }

  /**
   * A one-click shortcut to the {@link terms.loginGroup.plural} page, beside the menu button.
   *
   * <p>It is here because that page is opened far more often than anything else AGWA staff reach
   * for, and going through the menu to get to it every time is a click that buys nothing.</p>
   *
   * <p>RESOLVED THROUGH THE REGISTRY'S VISIBILITY FILTER rather than rendered unconditionally, so
   * the shortcut can never offer a page the person cannot open. It also takes the module's own
   * route, title and icon rather than restating them, which is what stops this drifting the day the
   * companies page moves.</p>
   */
  private renderCompaniesShortcut() {
    if (!this.userProfile?.isLoggedIn) return nothing;

    const [companies] = resolveVisibleModuleIds(['members'], {
      access: this.access,
      isLoggedIn: this.isAuthenticated,
    });
    if (!companies) return nothing;

    // Same pill as the menu button beside it, and labelled: there is room for the word, and an icon
    // alone makes a person guess. The word is the module's own title, which is the glossary term, so
    // a rename of the domain word moves this with it.
    return html`<sl-button
      size="small"
      pill
      href=${resolveRouterPath(companies.route)}
      class="modules-btn"
      variant="default"
    >
      <sl-icon name=${companies.iconName || 'people'} slot="prefix"></sl-icon>
      ${companies.title}
    </sl-button>`;
  }

  private renderHeaderActions() {
    const avatar = awaitableTemplate(async () => {
      const url = await this.profileImageUrl();
      return html`<sl-avatar
        class="profile-dropdown"
        .image=${url}
        label=${
          this.userProfile?.isLoggedIn
            ? this.userProfile.displayName
            : terms.loginUser.value
        }
      ></sl-avatar>`;
    });

    return html`
      <div class="header-actions">
        ${this.renderCompaniesShortcut()}
        ${
          this.userProfile?.isLoggedIn
            ? html`<sl-button
                size="small"
                pill
                href=${resolveRouterPath('menu')}
                class="modules-btn"
                variant="default"
              >
                <sl-icon name="grid" slot="prefix"></sl-icon> Menu
              </sl-button>`
            : null
        }
        <sl-dropdown distance="6" hoist class="user-menu">
          <div
            slot="trigger"
            class="user-menu-trigger"
            role="button"
            tabindex="0"
          >
            ${avatar}
            ${
              this.userProfile?.isLoggedIn
                ? html`<span class="username-label"
                    >${this.userProfile.displayName}</span
                  >`
                : null
            }
            <sl-icon name="chevron-down"></sl-icon>
          </div>
          <sl-menu @click=${(e: Event) => e.stopPropagation()}>
            ${
              this.userProfile?.isLoggedIn
                ? html`
                    <sl-menu-item @click=${() => this.navigate('')}>
                      <sl-icon slot="prefix" name="house"></sl-icon>
                      ${write`Home`}
                    </sl-menu-item>
                    <sl-menu-item @click=${() => this.navigate('user/profile')}>
                      <sl-icon slot="prefix" name="person"></sl-icon>
                      ${write`Profile`}
                    </sl-menu-item>
                    <sl-menu-item @click=${() => this.navigate('menu')}>
                      <sl-icon slot="prefix" name="grid"></sl-icon>
                      Menu
                    </sl-menu-item>
                    <sl-divider></sl-divider>
                    ${this.renderSwitchAccountSection()}
                    <sl-menu-item @click=${() => this.navigate('about')}>
                      <sl-icon slot="prefix" name="info-circle"></sl-icon>
                      ${write`About`}
                    </sl-menu-item>
                    <sl-menu-item @click=${() => this.handleLogout()}>
                      <sl-icon slot="prefix" name="box-arrow-right"></sl-icon>
                      ${write`Logout`}
                    </sl-menu-item>
                  `
                : html`
                    <sl-menu-item @click=${() => this.navigate('login')}>
                      <sl-icon
                        slot="prefix"
                        name="box-arrow-in-right"
                      ></sl-icon>
                      ${write`Login`}
                    </sl-menu-item>
                    <sl-menu-item @click=${() => this.navigate('')}>
                      <sl-icon slot="prefix" name="house"></sl-icon>
                      ${write`Home`}
                    </sl-menu-item>
                    <sl-divider></sl-divider>
                    <sl-menu-item @click=${() => this.navigate('about')}>
                      <sl-icon slot="prefix" name="info-circle"></sl-icon>
                      ${write`About`}
                    </sl-menu-item>
                  `
            }
          </sl-menu>
        </sl-dropdown>
        <sse-client-activity-monitor></sse-client-activity-monitor>
      </div>
    `;
  }

  // Network status is now rendered in renderHeaderActions

  // Drawer removed.

  // Drawer panel removed.

  render() {
    return html`
      <header class="app-header" role="banner" id="appHeaderRoot">
        <div class="header-row row-primary">
          ${this.renderBrandBlock()}
          <div class="spacer"></div>
          ${this.renderHeaderActions()}
        </div>
        <!-- Second row title (xs/sm). Hidden at md+ via CSS -->
        ${
          this.pageTitle
            ? html`<div class="header-row row-secondary">
                <h1 class="page-title-responsive">${this.pageTitle}</h1>
              </div>`
            : null
        }
      </header>
    `;
  }
}
